import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface State {
  error: Error | null
  /** Bumped by Redraw, so the canvas mounts again with fresh DOM. */
  attempt: number
}

/**
 * Keeps a failure inside the Form Builder canvas from taking the page down
 * (z8nrz7e1wt). Without it, a DOM error while the canvas re-rendered reached
 * React Router's "Unexpected Application Error!" page, and every unsaved edit
 * went with it.
 *
 * The form being edited lives in FormEditorPage, above this boundary, so
 * nothing is lost: the canvas shows a message instead, and Redraw mounts it
 * again from that state.
 */
export class CanvasErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, attempt: 0 }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Form Builder canvas failed', error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <div className='flex flex-1 items-start justify-center overflow-y-auto bg-muted/30 p-8'>
          <div
            role='alert'
            className='flex max-w-lg items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'
          >
            <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
            <div className='space-y-2'>
              <p>The form canvas hit a problem. Your changes are kept.</p>
              <Button
                size='sm'
                variant='outline'
                onClick={() => this.setState(s => ({ error: null, attempt: s.attempt + 1 }))}
              >
                <RotateCcw className='mr-1.5 h-3.5 w-3.5' />
                Redraw canvas
              </Button>
            </div>
          </div>
        </div>
      )
    }
    return <Fragment key={this.state.attempt}>{this.props.children}</Fragment>
  }
}
